import { X, Plus, Save } from "lucide-react";
import { Link } from "react-router-dom";

const INDUSTRIES = [
  "Technology", "Finance", "Real Estate", "Retail", "Hospitality",
  "Manufacturing", "Services", "Media", "Health", "Energy", "Other",
];
const SOCIALS = ["website", "instagram", "linkedin", "twitter", "youtube", "github", "facebook", "tiktok", "spotify"];

function LabeledInput({ label, value, onChange, placeholder, testid, maxLength, type = "text" }) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-[0.3em] text-neutral-500">{label}</span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        maxLength={maxLength}
        data-testid={testid}
        className="mt-3 w-full bg-transparent border-0 border-b border-white/15 focus:border-white/60 outline-none py-2.5 text-base font-light transition-colors placeholder:text-neutral-600"
      />
    </label>
  );
}

function LabeledTextarea({ label, value, onChange, placeholder, rows = 4, maxLength, testid, counter }) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-[0.3em] text-neutral-500">{label}</span>
      <textarea
        rows={rows}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        maxLength={maxLength}
        data-testid={testid}
        className="mt-3 w-full bg-transparent border border-white/[0.12] rounded-2xl focus:border-white/45 outline-none py-3 px-4 font-light transition-colors placeholder:text-neutral-600 text-sm"
      />
      {counter && <span className="text-[10px] text-neutral-500 tracking-wider mt-1 inline-block">{value.length} / {maxLength}</span>}
    </label>
  );
}

function Toggle({ on, onChange, disabled, testid }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      disabled={disabled}
      onClick={() => onChange(!on)}
      data-testid={testid}
      className={`inline-flex items-center py-2 ${disabled ? "opacity-40 cursor-not-allowed" : ""}`}
    >
      <span className={`relative flex h-6 w-11 items-center rounded-full transition-colors ${on ? "bg-white" : "bg-white/[0.12]"}`}>
        <span
          className={`inline-block h-4 w-4 rounded-full transition-transform ${on ? "bg-black translate-x-6" : "bg-white translate-x-1"}`}
        />
      </span>
    </button>
  );
}

export default function ProfileBody(p) {
  const { form, setField, addTag, removeTag, tagInput, setTagInput, save, saving, isPremium, tier, t } = p;

  return (
    <>
      {/* Public toggle */}
      <div className="rounded-3xl border border-white/[0.08] bg-[#0A0A0A]/80 p-8 flex flex-col sm:flex-row sm:items-center justify-between gap-6">
        <div className="max-w-2xl">
          <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-500">{t("vc.account.visibility")}</div>
          <div className="font-serif text-2xl mt-2">{form.is_public ? t("vc.account.public") : t("vc.account.private")}</div>
          <p className="text-sm text-neutral-400 font-light mt-2 leading-relaxed">
            {isPremium
              ? (form.is_public ? t("vc.account.public-hint") : t("vc.account.private-hint"))
              : t("vc.account.upgrade-hint")}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-[11px] tracking-[0.25em] uppercase text-neutral-500">{tier.toUpperCase()}</span>
          <Toggle
            on={form.is_public && isPremium}
            onChange={(v) => setField("is_public", v)}
            disabled={!isPremium}
            testid="visibility-toggle"
          />
        </div>
      </div>

      {/* Identity */}
      <div className="mt-6 rounded-3xl border border-white/[0.08] bg-[#0A0A0A]/80 p-8 lg:p-12 space-y-10">
        <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-500">{t("vc.account.identity")}</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-8">
          <LabeledInput label={t("vc.account.full-name")} value={form.name} onChange={(v) => setField("name", v)} maxLength={80} testid="profile-name-input" />
          <LabeledInput label={t("vc.account.headline")} value={form.headline} onChange={(v) => setField("headline", v)} placeholder={t("vc.account.headline-hint")} maxLength={140} testid="profile-headline-input" />
          <LabeledInput label={t("vc.account.portrait-url")} value={form.picture} onChange={(v) => setField("picture", v)} placeholder="https://…" testid="profile-picture-input" />
          <LabeledInput label={t("vc.account.location")} value={form.location} onChange={(v) => setField("location", v)} placeholder="London, UK" testid="profile-location-input" />
        </div>
        <LabeledTextarea label={t("vc.bio")} value={form.bio} onChange={(v) => setField("bio", v)} rows={5} maxLength={2000} placeholder={t("vc.account.bio-placeholder")} testid="profile-bio-input" counter />
      </div>

      {/* Business */}
      <div className="mt-6 rounded-3xl border border-white/[0.08] bg-[#0A0A0A]/80 p-8 lg:p-12 space-y-10">
        <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-500">{t("vc.account.business")}</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-8">
          <LabeledInput label={t("vc.account.company")} value={form.company} onChange={(v) => setField("company", v)} maxLength={120} testid="profile-company-input" />
          <LabeledInput label={t("vc.account.role")} value={form.role} onChange={(v) => setField("role", v)} placeholder={t("vc.account.role-hint")} maxLength={120} testid="profile-role-input" />
          <label className="block">
            <span className="text-[10px] uppercase tracking-[0.3em] text-neutral-500">{t("vc.account.industry")}</span>
            <select
              value={form.industry}
              onChange={(e) => setField("industry", e.target.value)}
              data-testid="profile-industry-select"
              className="mt-3 w-full bg-transparent border-0 border-b border-white/15 focus:border-white/60 outline-none py-2.5 text-base font-light appearance-none cursor-pointer"
            >
              <option value="" className="bg-[#0A0A0A]/80">{t("vc.account.select")}</option>
              {INDUSTRIES.map((i) => (
                <option key={i} value={i} className="bg-[#0A0A0A]/80">{i}</option>
              ))}
            </select>
          </label>
          <LabeledInput label={t("vc.account.website")} value={form.website} onChange={(v) => setField("website", v)} placeholder="https://…" testid="profile-website-input" />
        </div>

        {/* Expertise tags */}
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-500">{t("vc.account.expertise")}</div>
          <div className="mt-4 flex flex-wrap gap-2">
            {form.expertise.map((tag) => (
              <span key={tag} className="inline-flex items-center gap-2 border border-white/[0.15] rounded-full px-3 py-1.5 text-sm font-light">
                {tag}
                <button onClick={() => removeTag(tag)} className="-mr-1 px-1.5 py-2 text-neutral-500 hover:text-white" data-testid={`tag-remove-${tag}`}>
                  <X size={12} />
                </button>
              </span>
            ))}
            <div className="inline-flex items-center gap-2 border border-white/[0.10] rounded-full px-3 py-1.5">
              <input
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addTag(); } }}
                placeholder={t("vc.account.tag-placeholder")}
                data-testid="profile-tag-input"
                className="bg-transparent outline-none text-sm font-light placeholder:text-neutral-600 w-40"
              />
              <button onClick={addTag} className="px-1.5 py-2 text-neutral-500 hover:text-white" data-testid="profile-tag-add">
                <Plus size={14} />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Matching prefs */}
      <div className="mt-6 rounded-3xl border border-white/[0.08] bg-[#0A0A0A]/80 p-8 lg:p-12 space-y-8">
        <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-500">{t("vc.account.matching")}</div>
        <p className="text-sm text-neutral-400 font-light -mt-4 leading-relaxed">
          {isPremium ? t("vc.account.matching-hint") : t("vc.account.matching-locked")}
        </p>
        <LabeledTextarea label={t("vc.account.offering")} value={form.offering} onChange={(v) => setField("offering", v)} rows={3} maxLength={600} placeholder={t("vc.account.offering-hint")} testid="profile-offering-input" />
        <LabeledTextarea label={t("vc.account.looking-for")} value={form.looking_for} onChange={(v) => setField("looking_for", v)} rows={3} maxLength={600} placeholder={t("vc.account.looking-for-hint")} testid="profile-looking-input" />
      </div>

      {/* Social links */}
      <div className="mt-6 rounded-3xl border border-white/[0.08] bg-[#0A0A0A]/80 p-8 lg:p-12">
        <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-500 mb-6">{t("vc.account.social-links")}</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
          {SOCIALS.map((platform) => (
            <div key={platform} className="grid grid-cols-[6rem_1fr] gap-3 items-center">
              <span className="capitalize text-[11px] tracking-[0.2em] uppercase text-neutral-400">{platform}</span>
              <input
                value={form.social_links[platform] || ""}
                onChange={(e) => setField("social_links", { ...form.social_links, [platform]: e.target.value })}
                placeholder="https://…"
                data-testid={`profile-link-${platform}`}
                className="w-full bg-transparent border-0 border-b border-white/[0.10] focus:border-white/50 outline-none py-2 text-sm font-light transition-colors placeholder:text-neutral-600"
              />
            </div>
          ))}
        </div>
      </div>

      <div className="mt-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <button
          onClick={save}
          disabled={saving}
          data-testid="profile-save-btn"
          className="inline-flex items-center gap-3 px-8 py-4 rounded-full bg-white text-black text-[12px] tracking-[0.25em] uppercase font-medium hover:bg-neutral-200 disabled:opacity-50 transition-colors"
        >
          <Save size={14} />
          {saving ? t("vc.account.saving") : t("vc.account.save")}
        </button>
        {isPremium && (
          <Link
            to="/matching"
            className="inline-flex items-center gap-3 border border-white/25 text-white px-8 py-4 rounded-full text-[12px] tracking-[0.25em] uppercase hover:bg-white/5 hover:border-white/55 transition-colors"
            data-testid="account-open-matching"
          >
            {t("vc.matching.open")}
          </Link>
        )}
      </div>
    </>
  );
}
